import { mergeProps } from "@dirootie-extensions/javascript";
import _ from "lodash";
import { CSSProperties } from "react";
import { Button, BUTTON_COLOR } from "taltech-styleguide";
import { ButtonProps, ButtonVisualType } from "./Buttons.types";
import { IconTalTech } from "@components-common/icons";
import { defaultButtonKindDefinition } from "./Buttons.defaults";
import { NavLink } from "react-router-dom";
import { cond, equals, propEq, T } from "ramda";
import { isNotEmpty } from "ramda-adjunct";
import { Kvp } from "@dirootie-extensions/typescript";
import { Action } from "@dirootie-web/components-types";

const defaultButtonProps: ButtonProps = {
  kind: undefined,
  text: "Text missing",
  visual: "text",
  isDisabled: false,
  color: BUTTON_COLOR.SECONDARY,
  onClick: (data?: any, ev?: any) => ({}),
};

const createProps = (
  defaultProps: ButtonProps,
  props: Partial<ButtonProps<Action>>
): ButtonProps<Action> => {
  const deftProps = {
    ..._.cloneDeep(defaultProps),
    key: props.id ?? props.kind,
  } as ButtonProps<Action>;
  const { kind } = props;
  if (_.isUndefined(kind)) {
    const result = mergeProps(deftProps, props! as any) as ButtonProps<Action>;
    return result;
  }
  const kindDefaults = (defaultButtonKindDefinition as any)[kind as any];
  if (_.isUndefined(kindDefaults)) {
    return mergeProps(deftProps, props! as any) as ButtonProps<Action>;
  }
  const kindFinalDefaults = mergeProps(deftProps, [
    kindDefaults as any,
    props!,
  ]);
  return kindFinalDefaults as ButtonProps<Action>;
};

const colorMapper = (() => {
  const mapper = {
    primary: BUTTON_COLOR.SECONDARY,
    secondary: BUTTON_COLOR.PRIMARY,
    basic: BUTTON_COLOR.OUTLINE_PRIMARY,
    warn: BUTTON_COLOR.OUTLINE_SECONDARY,
    info: BUTTON_COLOR.OUTLINE_SECONDARY,
    success: BUTTON_COLOR.OUTLINE_SECONDARY,
  };

  const getColor = (value: string | undefined) => {
    if (value === undefined) {
      return mapper.primary;
    }
    if ((mapper as any)[value] === undefined) {
      return value;
    }
    return (mapper as any)[value];
  };

  return getColor;
})();

const ButtonInner: React.FC<any> = (props: ButtonProps) => {
  const { color, style, text, size = "sm", as, isDisabled, ...rest } = props;
  const label = props.label ? props.label : text;
  return (
    <Button
      key={props.id ?? props.kind}
      {...rest}
      disabled={isDisabled}
      variant={colorMapper(color) as BUTTON_COLOR}
      style={style as any}
      size={size as any}
    >
      {label}
    </Button>
  );
};

const typeSwitcher = (props: ButtonProps) => {
  const { visual = "text" } = props;
  const result = cond([
    [(_) => equals(visual, "icon"), (p) => <IconTalTech {...(p as any)} />],
    [
      (_: any) => equals(visual, "text"),
      (p: any) => <ButtonInner {...(p as any)} />,
    ],
    [
      (_: any) => equals(visual, "link"),
      (p: any) => <NavLink to={(props as any).actionToDispatch.payload.path} />,
    ],
    //[propEq(visual,"both"), () => <IconTalTech {...(props as any)} />],
    [T, () => <IconTalTech {...(props as any)} />],
  ])(props);
  return result;
};

const componentPerVisual: { [K in ButtonVisualType]: React.ComponentType } = {
  icon: IconTalTech,
  text: ButtonInner,
  both: ButtonInner,
};
const getComponent = (props: ButtonProps<Action>) => {
  const { visual = "text" } = props;
  return componentPerVisual[visual as ButtonVisualType];
};
export const ButtonTT: React.FC<ButtonProps<Action>> = (props) => {
  const btnProps = createProps(defaultButtonProps, props);

  const Component = getComponent(btnProps);
  const result = <Component {...btnProps} />;
  return result;
};
